import { useQueryPlans, useQueryAccountPlan } from '../../../hooks';
import {
  useQueryAccount,
  useQuerySession,
  useQueryAccountUsage,
} from '../../../../universal-nav/hooks';
import { getNextPlan } from './getNextPlan';

import type { CurrentAccount, Plan } from '../../../../types';
import type { ZapierSession } from '../../../../types';

export type ResponseData = {
  isAuthenticated: boolean;
  currentUser: ZapierSession;
  currentAccount: CurrentAccount;
  possiblePlans: Array<{
    planType: string;
    plans: Array<Plan>;
  }>;
  nextPlan?: Plan;
  nextTeamPlan?: Plan;
  nextCompanyPlan?: Plan;
};

type AlertBannerResult =
  | AlertBannerLoadingResult
  | AlertBannerErrorResult
  | AlertBannerSuccessResult
  | AlertBannerNoDataResult;

type AlertBannerLoadingResult = {
  data: undefined;
  isLoading: true;
  isError: false;
};

type AlertBannerErrorResult = {
  data: undefined;
  isLoading: false;
  isError: true;
};

type AlertBannerSuccessResult = {
  data: ResponseData;
  isLoading: false;
  isError: false;
};

type AlertBannerNoDataResult = {
  data: undefined;
  isLoading: false;
  isError: false;
};

export const useAlertBannerData = (): AlertBannerResult => {
  const {
    data: sessionData,
    isLoading: isLoadingSessionData,
    isError: isErrorSessionData,
  } = useQuerySession();
  const {
    data: accountData,
    isLoading: isLoadingAccountData,
    isError: isErrorAccountData,
  } = useQueryAccount(sessionData?.currentAccountId);
  const {
    data: planGroupsData,
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
  } = useQueryPlans(sessionData?.currentAccountId);
  const {
    data: accountUsageData,
    isLoading: isLoadingAccountUsageData,
    isError: isErrorAccountUsageData,
  } = useQueryAccountUsage(sessionData?.currentAccountId);
  const {
    data: accountPlanData,
    isLoading: isLoadingAccountPlanData,
    isError: isErrorAccountPlanData,
  } = useQueryAccountPlan(sessionData?.currentAccountId, accountData?.plan?.id);

  const currentPlan = Array.from(planGroupsData || [])
    ?.find((group) => group.planType === accountData?.plan.name)
    ?.plans.find((plan) => plan.id === accountData?.plan.id);

  const isLoading =
    isLoadingAccountData ||
    isLoadingSessionData ||
    isLoadingPlans ||
    isLoadingAccountUsageData ||
    isLoadingAccountPlanData;

  const isError =
    isErrorAccountData ||
    isErrorSessionData ||
    isErrorPlans ||
    isErrorAccountUsageData ||
    isErrorAccountPlanData;

  const isAllDataAvailable =
    !!sessionData &&
    !!accountData &&
    !!planGroupsData &&
    !!accountUsageData &&
    !!currentPlan &&
    !!accountPlanData;

  if (isLoading) {
    return {
      isError: false,
      isLoading: true,
      data: undefined,
    };
  }

  if (isError) {
    return {
      isError: true,
      isLoading: false,
      data: undefined,
    };
  }

  if (!isAllDataAvailable) {
    return {
      isError: false,
      isLoading: false,
      data: undefined,
    };
  }

  const currentPlanSpecifics = {
    actions_used: accountUsageData.tasks.total,
    interval: accountPlanData.interval,
    plan_type: accountPlanData.planType,
    total_actions: accountPlanData.actions,
  };

  const alertBannerData: ResponseData = {
    isAuthenticated: sessionData.isLoggedIn,
    currentUser: sessionData,
    currentAccount: {
      id: accountData.id,
      currency: accountData.currency,
      currentUserRole: sessionData.currentUserRole,
      firstKnownUpgradeDate: accountData.firstKnownUpgradeDate,
      isUnlimited: accountData.isUnlimited,
      hasOverdueInvoice: accountData.hasOverdueInvoice,
      periodEndDate: accountUsageData.periodEnd,
      isTrialing: accountData.isTrialing,
      trialEndDate: accountData.trialEndDate,
      isTeamAccount: accountData.isTeamAccount,
      isTrialingTeams: accountData.isTrialingTeams,
      teamAccountEndDate: accountData.teamAccountEndDate,
      teamsTrialAutoUpgrade: accountData.teamsTrialAutoUpgrade,
      teamTrialEndDate: accountData.teamTrialEndDate,
      isCompanyAccount: accountData.isCompanyAccount,
      isTrialingCompanies: accountData.isTrialingCompanies,
      companiesTrialEndDate: accountData.companiesTrialEndDate,
      companiesTrialAutoUpgrade: accountData.companiesTrialAutoUpgrade,
      plan: currentPlan,
    },
    possiblePlans: planGroupsData,
    nextPlan: getNextPlan(planGroupsData, currentPlanSpecifics, {
      nextPlanInterval: 'year',
    }),
    nextTeamPlan: getNextPlan(planGroupsData, currentPlanSpecifics, {
      nextPlanType: 'Team',
    }),
    nextCompanyPlan: getNextPlan(planGroupsData, currentPlanSpecifics, {
      nextPlanInterval: 'year',
      nextPlanType: 'Company',
    }),
  };

  return {
    data: alertBannerData,
    isLoading: false,
    isError: false,
  };
};
